import React, { ReactElement, useState } from 'react';

import {
  ClickAwayListener,
  Fade,
  Tooltip as MuiTooltip,
  styled,
  TooltipProps,
} from '@mui/material';

import { CloseButton } from '../buttons/close-button';

export const Tooltip = ({
  title,
  description,
  children,
  placement,
}: React.PropsWithChildren<{
  children: ReactElement;
  title?: string;
  description: React.ReactNode;
  placement?: TooltipProps['placement'];
}>) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <MuiTooltip
        aria-label="tooltip"
        disableFocusListener
        disableHoverListener
        disableTouchListener
        onClose={() => setOpen(false)}
        placement={placement}
        open={open}
        TransitionComponent={Fade}
        onClick={() => setOpen((oldValue) => !oldValue)}
        arrow={true}
        title={
          <TooltipComponent
            title={title}
            description={description}
            onClose={() => setOpen(false)}
          />
        }
      >
        {children}
      </MuiTooltip>
    </ClickAwayListener>
  );
};

const TooltipComponent = ({
  title,
  description,
  onClose,
}: {
  title: string | undefined;
  description: React.ReactNode;
  onClose: () => void;
}) => {
  return (
    <Container>
      <TextWrapper>
        {title && <Title>{title}</Title>}

        <Description>{description}</Description>
      </TextWrapper>

      <IconWrapper onClick={onClose} />
    </Container>
  );
};

const IconWrapper = styled(CloseButton)(({ theme }) => ({
  alignSelf: 'flex-start',
  marginTop: '-7.5px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '-8.5px',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '-10px',
  },
}));

const TextWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  ...theme.typography.footnote,
}));

const Title = styled('p')(({ theme }) => ({
  ...theme.typography.footnote,
  fontWeight: theme.typography.fontWeightMedium,
  marginBottom: '14px',
  marginTop: 0,
}));

const Description = styled('p')(({ theme }) => ({
  ...theme.typography.footnote,
  fontWeight: theme.typography.fontWeightRegular,
  marginTop: 0,
}));

export default Tooltip;
